import { cloneElement, isValidElement, memo, useRef } from 'react';

import { flexRender } from '@tanstack/react-table';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  InfoButton,
  TableCell as _TableCell,
  tableCellClasses,
  tableClasses,
} from '@kc-console/common-ui';
import { np } from '@kc-console/core';

import DataTableTableCellContent from './DataTableTableCellContent';

import type { JSX, MouseEvent, TouchEvent } from 'react';
import type { RowData } from '@tanstack/react-table';
import type { InfoButtonProps } from '@kc-console/common-ui';
import type { DataTableHeaderContent } from './types';

export interface DataTableTableHeadCellProps<TData extends RowData, TValue = unknown> {
  header: DataTableHeaderContent<TData, TValue>;
  onColumnResize: (
    event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>,
    columnId: string,
    cellElement: HTMLTableCellElement,
  ) => void;
  onColumnSizeReset: (columnId: string) => void;
  onSortDirectionChange: (columnId: string, sortDirection: 'asc' | 'desc' | false) => void;
}

export default memo(function DataTableTableHeadCell<TData extends RowData, TValue = unknown>(
  props: DataTableTableHeadCellProps<TData, TValue>,
) {
  const { header, onSortDirectionChange, onColumnResize, onColumnSizeReset } = props;
  const {
    CellTemplate,
    cellTemplateProps,
    colSpan,
    id,
    isPlaceholder,
    maxSize,
    meta,
    minSize,
    offset,
    resizable,
    sortable,
    sortDirection,
    stickyPosition,
    width,
  } = header;

  const {
    column: { align } = {},
    header: { info, required = false } = {},
    cell: { padding } = {},
  } = meta ?? {};

  const ref = useRef<HTMLTableCellElement | null>(null);

  const infoButtonDefaultProps = {
    size: 'sm',
    color: 'default',
  } as const;

  const infoButtonElement =
    typeof info === 'string' ? (
      <InfoButton {...infoButtonDefaultProps} tooltipProps={{ message: info }} />
    ) : isValidElement<InfoButtonProps>(info) ? (
      cloneElement(info, { ...infoButtonDefaultProps, ...info.props })
    ) : (
      info
    );

  const handleColumnResize = (event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
    if (ref.current) {
      onColumnResize(event, header.id, ref.current);
    }
  };

  if (isPlaceholder) {
    return <TableCell key={id} colSpan={colSpan} />;
  }

  return (
    <TableCell
      key={id}
      ref={ref}
      align={align}
      colSpan={colSpan}
      endAdornment={infoButtonElement}
      useSortLabel={sortable}
      sortDirection={header.sortDirection}
      slots={{ content: DataTableTableCellContent }}
      tableSortLabelComponentProps={{
        onClick: () =>
          onSortDirectionChange(
            id,
            !sortDirection ? 'asc' : sortDirection === 'asc' ? 'desc' : false,
          ),
      }}
      padding={padding}
      resizable={resizable}
      resizeHandler={handleColumnResize}
      resizeResetHandler={() => onColumnSizeReset(header.id)}
      sizes={{
        width: width,
        maxWidth: maxSize,
        minWidth: minSize,
      }}
      sticky={!!stickyPosition}
      stickyPosition={stickyPosition || undefined}
      style={{ ...(stickyPosition ? { [stickyPosition]: offset } : {}) }}
    >
      {flexRender(CellTemplate, cellTemplateProps)}
      {required && ' *'}
    </TableCell>
  );
}) as <TData extends RowData>(props: DataTableTableHeadCellProps<TData>) => JSX.Element;

const TableCell = styled(_TableCell, { shouldForwardProp: np('sizes') })<{
  sizes?: { maxWidth: number | undefined; minWidth: number | undefined; width: number | string };
}>(({ sizes }) => [
  sizes &&
    css`
      width: ${sizes.width};
      ${sizes.minWidth && `min-width: ${sizes.minWidth}px`};
      ${sizes.maxWidth && `max-width: ${sizes.maxWidth}px`};
      .${tableClasses.root}[data-show-side-margin='true'] &:is(:first-of-type, :last-of-type) {
        width: calc(${sizes.width} + 20px);
        ${sizes.minWidth && `min-width: calc(${sizes.minWidth}px + 20px);`}
        ${sizes.maxWidth && `max-width: calc(${sizes.maxWidth}px + 20px);`}
      }
    `,
  css`
    & .${tableCellClasses.content} {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
]);
